// Minimal promise wrapper over IndexedDB for the cart. No npm runtime deps.
//
// DB "synthbash", object store "cart" (keyPath "id", autoIncrement). One record
// per cart item: the scene it was configured from, how many to print, and a
// thumbnail of the viewer as it looked when the item was added. The STLs are
// built at download time, so nothing bulky is stored.

export type CartItem = {
  id?: number; // assigned by IndexedDB on add
  // The scene this item was configured from. Deterministic (stable param order
  // from Rust), so string equality is the canonical identity for dedupe.
  sceneJson: string;
  label: string;
  quantity: number;
  // Small JPEG of the view the item was added from; absent when the capture
  // wasn't available, so every reader needs a fallback.
  thumbnail?: Blob;
};

const DB_NAME = "synthbash";
const STORE = "cart";
// The record shape's version; the upgrade drops records written by older ones.
const DB_VERSION = 4;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      // Older record shapes are incompatible; drop old carts and recreate.
      if (db.objectStoreNames.contains(STORE)) db.deleteObjectStore(STORE);
      db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
    };
    req.onsuccess = () => {
      const db = req.result;
      // Hold the connection open no longer than another tab's upgrade needs:
      // an open one is exactly what blocks it. The close lets running
      // transactions finish, and the next call reopens at the new version.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
    // Another tab holding an older version open blocks the upgrade, and the
    // open would otherwise sit there unresolved until that tab goes away.
    req.onblocked = () => {
      // Nothing awaits this open any more, so hand back the connection it may
      // still end up making.
      req.onsuccess = () => req.result.close();
      dbPromise = null;
      reject(new Error("another tab has an older cart open; close it and reload"));
    };
  });
  return dbPromise;
}

// Run `fn` inside a transaction and resolve when it commits.
async function tx<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | undefined> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    const store = transaction.objectStore(STORE);
    let result: T | undefined;
    const req = fn(store);
    if (req) req.onsuccess = () => (result = req.result);
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

export async function loadAll(): Promise<CartItem[]> {
  const items = await tx<CartItem[]>("readonly", (store) => store.getAll());
  return items ?? [];
}

// Add a new item; returns the id assigned by IndexedDB.
export async function addItem(item: CartItem): Promise<number> {
  const { id: _drop, ...rest } = item;
  const key = await tx<IDBValidKey>("readwrite", (store) => store.add(rest));
  return key as number;
}

export async function updateQuantity(id: number, quantity: number): Promise<void> {
  await tx("readwrite", (store) => {
    const get = store.get(id);
    get.onsuccess = () => {
      const item = get.result as CartItem | undefined;
      if (item) {
        item.quantity = quantity;
        store.put(item);
      }
    };
  });
}

export async function deleteItem(id: number): Promise<void> {
  await tx("readwrite", (store) => store.delete(id));
}

export async function clearAll(): Promise<void> {
  await tx("readwrite", (store) => store.clear());
}
